import { baseSplitApi } from "./index";

export type Services = number | number[];
export type ServicesWithCurrency = Record<string, Services>;

export interface PointManagerItem {
  available_components: {
    id: number;
    name: string;
    display_name: string;
    listing_order: number;
    parent: number;
  }[];
  available_wallets: {
    id: number;
    name: string;
    currency: string;
    status: number;
  }[];
  payout_wallets: {
    id: number;
    name: string;
    currency: string;
    status: number;
  }[];
  main_wallet: { id: number; name: string; currency: string };
  point: { id: number; name: string; department: number };
  point_id: number;
  access_level: { id: number; name: string };
  user: { id: number; username: string; account_id: number };
  id: number;
  is_admin: boolean;
  hide_inactive_wallets: boolean;
  service_config: {
    card_payouts: Services;
    default_phone_payout_service: ServicesWithCurrency;
    phone_payouts_available: boolean;
    settlepay_payouts: ServicesWithCurrency;
    card_top_up: Services;
    binance_top_up: ServicesWithCurrency;
    wallet_crypto_top_up: ServicesWithCurrency;
    binance_id_payouts: ServicesWithCurrency;
    exmo_create_code: ServicesWithCurrency;
    binance_create_code: ServicesWithCurrency;
    whitebit_create_code: ServicesWithCurrency;
    kuna_create_code: ServicesWithCurrency;
    pix_payouts: ServicesWithCurrency;
    upi_payouts: ServicesWithCurrency;
    invoicing_service: ServicesWithCurrency;
    payouts_custom_delay_interface: boolean;
    mobile_service_vodafone: Services;
    mobile_service_kyivstar: Services;
    mobile_service_life: Services;
    spei_payouts: Record<string, Services>;
    iban_payouts: Services;
  };
}

type PointManagerQueryParams = {
  page: string;
  page_size: string;
  search: string;
  point_id: number;
};

export const pointManagersApi = baseSplitApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    getPointManagers: builder.query<
      { count: number; results: PointManagerItem[] },
      PointManagerQueryParams
    >({
      query: (params) => {
        const query = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value) {
            query.append(key, String(value));
          } else {
            query.delete(key);
          }
        });
        return `/point-managers/?${query.toString()}`;
      },
      providesTags: ["PointManagers"],
    }),
    getPointManager: builder.query<PointManagerItem, { id: number }>({
      query: ({ id }) => `/point-managers/${id}/`,
      providesTags: ["PointManager"],
    }),
    createPointManager: builder.mutation<
      PointManagerItem,
      {
        available_components: number[];
        available_wallets: number[];
        email: string;
        access_level: number;
        point_id: number;
      }
    >({
      query: (data) => ({
        url: "/point-managers/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PointManagers", "PointManager"],
    }),
    updatePointManager: builder.mutation<
      PointManagerItem,
      {
        id: number;
        available_components: number[];
        available_wallets: number[];
        access_level: number;
        user: string;
        point_id: number;
      }
    >({
      query: ({ id, ...data }) => ({
        url: `/point-managers/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["PointManagers", "PointManager"],
    }),
    deletePointManager: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/point-managers/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["PointManagers", "PointManager"],
    }),

    getMerchantGroups: builder.query<
      { count: number; results: { id: number; name: string }[] },
      void
    >({
      query: () => {
        const query = new URLSearchParams();
        return `/merchant-groups/?${query.toString()}`;
      },
    }),

    getPointManagerByUser: builder.query<
      { count: number; results: PointManagerItem[] },
      { user: number; page_size: string }
    >({
      query: ({ user, page_size }) => {
        const query = new URLSearchParams();
        query.append("user", String(user));
        query.append("page_size", page_size);
        return `/point-managers/?${query.toString()}`;
      },
    }),

    switchHideInactiveWallets: builder.mutation<
      PointManagerItem,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/point-managers/${id}/switch-hide-inactive-wallets/`,
        method: "POST",
      }),
      invalidatesTags: ["PointManagers", "PointManager"],
    }),
  }),
});

export const {
  useGetPointManagersQuery,
  useLazyGetPointManagerQuery,
  useCreatePointManagerMutation,
  useUpdatePointManagerMutation,
  useDeletePointManagerMutation,
  useLazyGetPointManagerByUserQuery,
  useGetPointManagerByUserQuery,
  useGetMerchantGroupsQuery,
  useSwitchHideInactiveWalletsMutation,
} = pointManagersApi;
